import { useState, useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';
import { authStore } from '../../stores/authStore';
import accountCreatedIllustration from "../../assets/account-created.png";
import { CircleCheckBig, Loader2, TriangleAlert, X } from 'lucide-react';

const VerificationModal = ({ token, onClose }) => {
  const snap = useSnapshot(authStore);
  
  const [isVerifying, setIsVerifying] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [error, setError] = useState('');
  const startedToken = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const resentVerificationLink = async () => {
    try {
      const email = snap.user?.email;
      if (!email) {
        alert('Email not found. Please try signing up again.');
        return;
      }
      
      await authStore.resendVerificationEmail(email);
      alert('Verification email resent successfully! Please check your inbox.');
    } catch (error) {
      console.error('Failed to resend verification link:', error);
      alert(error?.message || 'Failed to resend verification link. Please try again.');
    }
  };

  useEffect(() => {
    if (!token || startedToken.current === token) return;
    startedToken.current = token;

    const verifyToken = async () => {
      try {
        const response = await authStore.verifyEmail(token);

        if (response && response.canLogin) {
          setVerificationStatus('success');
          setIsVerifying(false);

          setTimeout(() => {
            onCloseRef.current();
          }, 2000);
        } else {
          setError('Failed to verify your account. The link may be invalid or expired.');
          setVerificationStatus('error');
          setIsVerifying(false);
        }
      } catch (err) {
        console.error('Email verification failed:', err);
        setError(err?.message || 'Failed to verify your account. Please try again.');
        setVerificationStatus('error');
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const renderContent = () => {
    // Loading state while verifying
    if (isVerifying) {
      return (
        <div className="text-center space-y-6">
          <div className="flex justify-center">
            <Loader2 className="w-12 h-12 text-[var(--primary-font)] animate-spin" />
          </div>
          <h2 className="font-helvetica text-2xl font-semibold text-[var(--primary-font)]">
            Verifying Your Email
          </h2>
          <p className="text-sm text-gray-600">
            Please wait while we verify your email address...
          </p>
        </div>
      );
    }

    // Error state
    if (verificationStatus === 'error') {
      return (
        <div className="text-center space-y-6">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <TriangleAlert className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="font-helvetica text-2xl font-semibold text-red-600">
            Verification Failed
          </h2>
          <p className="text-sm text-gray-600">
            {error}
          </p>
          <div className="space-y-3">
            <button
              onClick={resentVerificationLink}
              className="w-full py-3 rounded-[1rem] font-medium text-md bg-[var(--primary-font)] text-white hover:bg-[var(--primary-font)]/90 transition-colors"
            >
              Resend Mail
            </button>
            <button
              onClick={onClose}
              className="w-full py-3 rounded-[1rem] font-medium text-md border border-[var(--primary-font)] text-[var(--primary-font)] hover:bg-[var(--primary-font)]/10 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      );
    }

    // Success state
    if (verificationStatus === 'success') {
      return (
        <div className="text-center space-y-6">
          {/* Success Icon */}
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
            <CircleCheckBig className="w-8 h-8 text-green-600" />
          </div>

          {/* Success Message */}
          <div>
            <h2 className="font-helvetica text-2xl font-bold text-[var(--primary-font)] mb-2">
              Email Verified Successfully!
            </h2>
            <p className="text-sm text-gray-600">
              Your account has been verified!
            </p>
          </div>

          {/* Illustration */}
          <div className="py-4">
            <img 
              src={accountCreatedIllustration} 
              alt="Email Verified" 
              className="w-full h-auto max-w-xs mx-auto" 
            />
          </div>

          {/* Auto redirect message */}
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-xs text-green-700">
              Modal will close automatically...
            </p>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white p-8 rounded-2xl shadow-lg max-w-md w-full mx-4 relative">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={isVerifying}
        >
          <X className="w-6 h-6" />
        </button>
        
        {renderContent()}
      </div>
    </div>
  );
};

export default VerificationModal; 